import {
	focusManager,
	type InvalidateOptions,
	type InvalidateQueryFilters,
	QueryClient,
	type QueryClientConfig,
} from "@tanstack/react-query";

export class MyPlannQueryClient extends QueryClient {
	#mountCount: number;
	#unsubscribeFocus?: () => void;

	constructor(config: QueryClientConfig) {
		super({ ...config });
		this.#mountCount = 0;
	}

	/**
	 * Override invalidateQueries to use refetchType: 'active' by default.
	 * This prevents "Missing queryFn" errors for queries restored from persistence
	 * that are not currently being observed by any component.
	 */
	override invalidateQueries(
		filters?: InvalidateQueryFilters,
		options?: InvalidateOptions,
	): Promise<void> {
		return super.invalidateQueries(
			{ refetchType: "active", ...filters },
			options,
		);
	}

	/**
	 * Overriding the mount method so the resumePausedMutations and queryCache.onOnline() are not called when connected to the network.
	 * That is handled by the useReconnect hook.
	 * This method should be same as the original mount method but without the onlineManager subscription.
	 * https://github.com/TanStack/query/blob/main/packages/query-core/src/queryClient.ts#L78
	 */
	mount(): void {
		this.#mountCount++;
		if (this.#mountCount !== 1) return;

		this.#unsubscribeFocus = focusManager.subscribe(async (focused) => {
			if (focused) {
				await this.resumePausedMutations();
			}
		});
	}

	unmount(): void {
		this.#mountCount--;
		if (this.#mountCount !== 0) return;

		this.#unsubscribeFocus?.();
		this.#unsubscribeFocus = undefined;
	}
}
